import { DynamicService } from 'modules/dynamic-service';
import { AppState } from 'store';
import * as dynamic from 'utils/dynamic';
import { apiRtk } from 'utils/service';
import { apiTodoTaskCategories, TaskTag } from '../todo-task-categories';
import { API_TODO_TASKS, ToDoTask, ToDoTaskPostInput } from './models';

class Service extends DynamicService<ToDoTask> {
  create = async (input: ToDoTaskPostInput) => {
    const { userEmployeeProfileIDs, ...rest } = input;
    const response = await this.post(rest);

    const id = String(response.data.id);

    if (userEmployeeProfileIDs.length) {
      await this.bulkUserEmployees(id, userEmployeeProfileIDs);
    }

    return { data: response.data };
  };
  bulkUserEmployees = async (id: string, userEmployeeProfileIDs: string[]) => {
    return this.engine.post(API_TODO_TASKS.BULK_EMPLOYEE_IDS, {
      mainID: id,
      itemIDs: userEmployeeProfileIDs,
    });
  };
}

export const ServiceTodoTasks = new Service({
  mainField: 'id',
  getAll: API_TODO_TASKS.GET_ALL_DYNAMIC,
  post: API_TODO_TASKS.POST,
  patch: API_TODO_TASKS.PATCH,
  delete: API_TODO_TASKS.DELETE,
});

export const apiTodoTasks = apiRtk.injectEndpoints({
  endpoints: (builder) => ({
    createTodoTask: builder.mutation<ToDoTask, ToDoTaskPostInput>({
      queryFn: async (input) => {
        try {
          const { data } = await ServiceTodoTasks.create(input);

          return { data };
        } catch (e: any) {
          return { error: e };
        }
      },
    }),
    createAutoTodoTaskRenewal: builder.mutation<void, string>({
      queryFn: async (input, { getState, dispatch }) => {
        try {
          const state = getState() as AppState;
          const user = state.accounts.user;

          if (!user) {
            throw new Error('current user is not defined');
          }

          const resultRequest = dispatch(
            apiTodoTaskCategories.endpoints.getTodoTaskCategoryForTaskDataByTag.initiate(
              TaskTag.PatientAppAskPrescription,
            ),
          );

          resultRequest.unsubscribe();
          const category = await resultRequest.unwrap();

          const message = category.defaultMessage
            ? `${category.defaultMessage}:
            "${input}"
          `
            : `"${input}"`;

          const payload = {
            userPatientProfileID: user.appUserID,
            toDoTaskCategoryID: category.id,
            entryDate: new Date().toISOString(),
            message: message,
            userEmployeeProfileIDs: category.userEmployeeProfileIDs,
          };

          await dispatch(apiTodoTasks.endpoints.createTodoTask.initiate(payload)).unwrap();

          return { data: undefined };
        } catch (e: any) {
          return { error: e };
        }
      },
    }),
    createAutoTodoTaskByTag: builder.mutation<void, TaskTag>({
      queryFn: async (taskTag, { getState, dispatch }) => {
        try {
          const state = getState() as AppState;
          const user = state.accounts.user;

          if (!user) {
            throw new Error('current user is not defined');
          }

          const resultRequest = dispatch(
            apiTodoTaskCategories.endpoints.getTodoTaskCategoryForTaskDataByTag.initiate(taskTag),
          );

          resultRequest.unsubscribe();
          const category = await resultRequest.unwrap();

          const payload = {
            userPatientProfileID: user.appUserID,
            toDoTaskCategoryID: category.id,
            entryDate: new Date().toISOString(),
            message: category.defaultMessage,
            userEmployeeProfileIDs: category.userEmployeeProfileIDs,
          };

          await dispatch(apiTodoTasks.endpoints.createTodoTask.initiate(payload)).unwrap();

          return { data: undefined };
        } catch (e: any) {
          return { error: e };
        }
      },
    }),
    createAutoTodoTaskByTagIfNoneOpen: builder.mutation<void, TaskTag>({
      queryFn: async (taskTag, { getState, dispatch }) => {
        try {
          const state = getState() as AppState;
          const user = state.accounts.user;

          if (!user) {
            throw new Error('current user is not defined');
          }

          const {
            data: { value: unDoneTasks },
          } = await ServiceTodoTasks.getAllDynamic({
            take: 1,
            select: dynamic.select(
              'id',
              'userPatientProfileID',
              'toDoTaskCategory.taskTag',
              'done',
            ),
            filter: dynamic
              .mergeFilters(
                dynamic.makeFilter('userPatientProfileID', user.appUserID, dynamic.equals),
                dynamic.makeFilter('isActive', true, dynamic.equals),
                dynamic.makeFilter('done', false, dynamic.equals),
                dynamic.makeFilter('toDoTaskCategory.taskTag', taskTag, dynamic.equals),
              )
              .join('&&'),
          });

          if (unDoneTasks.length > 0) return { data: undefined };

          await dispatch(apiTodoTasks.endpoints.createAutoTodoTaskByTag.initiate(taskTag)).unwrap();

          return { data: undefined };
        } catch (e: any) {
          return { error: e };
        }
      },
    }),
  }),
});

export * from './models';
